import {
  signInWithRedirect,
  signIn,
  signUp,
  confirmSignUp,
  resetPassword,
  confirmResetPassword,
  signOut,
  getCurrentUser,
  fetchAuthSession,
  type SignUpInput,
  type ConfirmSignUpInput,
  type SignInInput,
  type ResetPasswordInput,
  type ConfirmResetPasswordInput,
} from 'aws-amplify/auth';

// OAuth login with Google
export async function loginWithGoogle(): Promise<void> {
  await signInWithRedirect({ provider: 'Google' });
}

// Email/Password sign in
export async function signInWithEmail(email: string, password: string): Promise<void> {
  const signInInput: SignInInput = {
    username: email,
    password,
  };
  await signIn(signInInput);
}

// Email/Password sign up
export async function signUpWithEmail(email: string, password: string): Promise<void> {
  const signUpInput: SignUpInput = {
    username: email,
    password,
    options: {
      userAttributes: {
        email,
      },
    },
  };
  await signUp(signUpInput);
}

// Confirm sign up with verification code
export async function confirmSignUpCode(email: string, code: string): Promise<void> {
  const confirmInput: ConfirmSignUpInput = {
    username: email,
    confirmationCode: code,
  };
  await confirmSignUp(confirmInput);
}

// Request password reset
export async function requestPasswordReset(email: string): Promise<void> {
  const resetInput: ResetPasswordInput = {
    username: email,
  };
  await resetPassword(resetInput);
}

// Confirm password reset with code
export async function confirmPasswordResetCode(
  email: string,
  code: string,
  newPassword: string
): Promise<void> {
  const confirmInput: ConfirmResetPasswordInput = {
    username: email,
    confirmationCode: code,
    newPassword,
  };
  await confirmResetPassword(confirmInput);
}

// Backward compatibility: keep login() for Google
export async function login(): Promise<void> {
  await loginWithGoogle();
}

export async function logout(): Promise<void> {
  await signOut();
}

export async function getIdToken(): Promise<string | undefined> {
  try {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
  } catch (error) {
    console.error('Error getting ID token:', error);
    return undefined;
  }
}

export async function getCurrentUserInfo() {
  try {
    const user = await getCurrentUser();
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
}

export async function isAuthenticated(): Promise<boolean> {
  try {
    await getCurrentUser();
    return true;
  } catch {
    return false;
  }
}
